import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type {
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  HovGainUpgradeMpTarget,
  HovGainUpgradeOneCostBatchPreview,
  NHovBranchId,
} from "../types";
import { NHovView } from "../nHov/nHovView";
import type { NHovExecutionState } from "../nHov/nHovExecutionAnimation";
import { NHovStagePanelState } from "../nHov/nHovStagePanelState";
import { HovSkillView } from "./hovSkillView";
import type { HovSkillSnapshot } from "./hovSkillModel";
import type {
  HovSkillHoldMs,
  HovSkillId,
  HovSkillResourceBalances,
} from "./hovSkillDefinitions";
import {
  resolveAmplifierGainOHovPreviewMultiplier,
  resolveOHovHovCoreGainPreviewMultiplier,
  resolveRaBambooAmplifierPreviewMultiplier,
  resolveRaPineAmplifierPreviewMultiplier,
} from "./hovSkillTooltipPreview";
import {
  resolveFreeMpCoreOHovHovCoreGainMultiplier,
  resolveMpCapacityCoreOHovHovCoreGainMultiplier,
} from "./hovSkillEffects";
import { HoverTargetGridView } from "@/features/hoverIdle/hover/hoverTargetGridView";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import {
  NoticeToastStack,
  type ShowToastOptions,
} from "@/features/hoverIdle/ui/noticeToastStack";
import { ResourceHudView } from "@/features/hoverIdle/resources/resourceHudView";
import { HovCoreHudView } from "../oHov/hovCoreHudView";
import {
  GroupingHovGainUpgradePanelView,
  type GroupingHovGainUpgradePurchaseOutcome,
} from "@/features/hoverIdle/upgrades/groupingHovGainUpgradePanelView";
import { buildGroupingHovGainUpgradeCatalog } from "@/features/hoverIdle/upgrades/groupingHovGainUpgradeCatalog";
import { MHovView } from "../mHov/mHovView";
import type { MHovSpeedSetting } from "../mHov/mHovView";
import { runMHovAnimation } from "../mHov/mHovAnimation";
import type { HoverIdleMotionPolicy } from "../core/hoverIdleMotionPolicy";
import {
  OHovProgressView,
  type OHovManualExecutionButtonVisual,
} from "../oHov/oHovProgressView";
import { type HugeNumberInput } from "@/shared/numbers";
import {
  HovAmplifierPanelView,
  shouldUnlockAmplifierUpgradeGuardUi,
} from "./hovAmplifierPanelView";
import type { HovAmplifierEntryId } from "./hovAmplifierModel";
import { computeMpmAvailableMpFromOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import type { AmplifierUpgradeGuardMs } from "../types";

export type CoreLoopTabId =
  | "main-hover"
  | "hov-skill"
  | "hov-amplifier"
  | "hov-wall"
  | "hov-exploration";

export interface HovViewUpdateOptions {
  readonly readoutSnapshot?: HoverIdleSnapshot;
  readonly targetStructureSnapshot?: HoverIdleSnapshot;
  readonly targetStructureAction?:
    | "commit-snapshot"
    | "hold-last-presentation-worthy";
  readonly updateTargetThemeHue?: boolean;
  readonly deferTargetStructureHoverRefresh?: boolean;
}

function isPresentationWorthyTargetStructure(
  snapshot: HoverIdleSnapshot,
): boolean {
  return snapshot.hovGridTier !== 1;
}

function withLatestTargetThemeHue(
  targetStructureSnapshot: HoverIdleSnapshot,
  latestSnapshot: HoverIdleSnapshot,
): HoverIdleSnapshot {
  return {
    ...targetStructureSnapshot,
    hovGridThemeHue: latestSnapshot.hovGridThemeHue,
  };
}

interface HovViewOptions {
  readonly container: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly onCellHoverChange: (
    cellIndex: number,
    isHovering: boolean,
  ) => boolean | void;
  readonly onPurchaseGlobalHovGainUpgrade: () => GroupingHovGainUpgradePurchaseOutcome;
  readonly onPurchaseHovCellGainUpgrade: (
    index: HovCellGainUpgradeIndex,
  ) => GroupingHovGainUpgradePurchaseOutcome;
  readonly resolveGlobalHovGainUpgradeOneCostBatchPreview?: () =>
    | HovGainUpgradeOneCostBatchPreview
    | undefined;
  readonly resolveHovCellGainUpgradeOneCostBatchPreview?: (
    index: HovCellGainUpgradeIndex,
  ) => HovGainUpgradeOneCostBatchPreview | undefined;
  readonly onHovGainUpgradePurchaseBurstStart?: () => void;
  readonly onHovGainUpgradePurchaseBurstEnd?: () => void;
  readonly getHovGainFamilyUpgradeRepeatInitialIntervalMs?: () => number;
  readonly getAmplifierUpgradeRepeatInitialIntervalMs?: () => number;
  readonly getUpgradeRepeatSpeedMultiplier?: () => number;
  readonly onHovSkillAcquire: (id: HovSkillId) => boolean;
  readonly getHovSkillSnapshot: () => HovSkillSnapshot;
  readonly getHovSkillHoldDurationMs: () => HovSkillHoldMs;
  readonly getShowLockedHovSkillContent?: () => boolean;
  readonly getHovAmplifierUnlocked: () => boolean;
  readonly getWallUnlocked?: () => boolean;
  readonly getExplorationAvailable?: () => boolean;
  readonly getOHovHovCoreGainScalingUnlocked?: () => boolean;
  readonly onHovSkillHoldDurationChange: (ms: HovSkillHoldMs) => void;
  readonly onActiveTabChange?: (tabId: CoreLoopTabId) => void;
  readonly onMpPlace?: (cellIndex: number) => void;
  readonly onMpRetrieve?: (cellIndex: number) => void;
  readonly onMpWallHovTargetAssign?: (cellIndex: number) => void;
  readonly onMpWallHovTargetRemove?: (cellIndex: number) => void;
  readonly onMpWallHovGainUpgradeAssign?: (
    target: HovGainUpgradeMpTarget,
  ) => void;
  readonly onMpWallHovGainUpgradeRemove?: (
    target: HovGainUpgradeMpTarget,
  ) => void;
  readonly onHovGainUpgradeMpAssign?: (target: HovGainUpgradeMpTarget) => void;
  readonly onHovGainUpgradeMpRemove?: (target: HovGainUpgradeMpTarget) => void;
  readonly onMHovExecute?: () => void | Promise<void>;
  readonly getMHovSpeed?: () => MHovSpeedSetting;
  readonly getMotionPolicy?: () => HoverIdleMotionPolicy;
  readonly getMHovChargeDurationMs?: () => number;
  readonly onNHovExecute?: (branchId: NHovBranchId) => boolean | void;
  readonly onOHovExecute?: () => void;
  readonly onLockHovInput?: (locked: boolean) => void;
  readonly onHovCellClick?: (cellIndex: number) => void;
  readonly onHovOLabelHoverChange?: (isHovering: boolean) => void;
  readonly getHovOLabelAchievementUnlocked?: () => boolean;
  readonly onTimHovActivationToggle?: () => void;
  readonly getTimHovActivationUnlocked?: () => boolean;
  readonly isHngDrawerActive?: () => boolean;
  readonly onHovAmplifierEntryHoverChange?: (
    entryId: HovAmplifierEntryId,
    isHovering: boolean,
  ) => void;
  readonly onPurchaseHovAmplifierGainUpgrade?: (
    entryId: HovAmplifierEntryId,
  ) => boolean;
  readonly onAmplifierPanelMpAssign?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierPanelMpRemove?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpAssign?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpRemove?: (entryId: HovAmplifierEntryId) => void;
  readonly getAmplifierUpgradeGuardMs?: () => AmplifierUpgradeGuardMs;
  readonly onAmplifierUpgradeGuardMsChange?: (
    value: AmplifierUpgradeGuardMs,
  ) => void;
  readonly getHasSeenAvailableAmplifierUpgradeGuard?: () => boolean;
  readonly onSeenAvailableAmplifierUpgradeGuard?: () => boolean;
  // Persistent "reset button displayed" latches. The view observes the same
  // MHov / NHov display flags it renders and latches once via these callbacks.
  readonly getHasSeenMHovButton?: () => boolean;
  readonly onSeenMHovButton?: () => boolean;
  readonly getHasSeenNHovButton?: () => boolean;
  readonly onSeenNHovButton?: () => boolean;
  // Hover-toggle duration setting (1 s / 2 s) for MP slot and TimHov toggles.
  readonly getHoverToggleDurationMs?: () => number;
}

export class HovView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly coreTabs: HTMLDivElement;
  private readonly mainTabButton: HTMLButtonElement;
  private readonly skillTabButton: HTMLButtonElement;
  private readonly amplifierTabButton: HTMLButtonElement;
  private readonly wallTabButton: HTMLButtonElement;
  private readonly explorationTabButton: HTMLButtonElement;
  private readonly wallPanel: HTMLDivElement;
  private readonly explorationPanel: HTMLDivElement;
  private readonly toastStack: NoticeToastStack;
  private readonly mainPanel: HTMLDivElement;
  private readonly hovStage: HTMLDivElement;
  private readonly hovCellsPanel: HTMLDivElement;
  private readonly nHovPanel: HTMLDivElement;
  private readonly hovStagePanelCaretButton: HTMLButtonElement;
  private readonly amplifierPanel: HovAmplifierPanelView;
  private readonly resourceHud: ResourceHudView;
  private readonly hovCoreHud: HovCoreHudView;
  private readonly targetGrid: HoverTargetGridView;
  private readonly groupingHovGainUpgradePanel: GroupingHovGainUpgradePanelView;
  private readonly mHovView: MHovView;
  private readonly nHovView: NHovView;
  private readonly oHovProgressView: OHovProgressView;
  private isHovInputLocked = false;
  private readonly skillView: HovSkillView;
  private readonly getHovSkillSnapshot: () => HovSkillSnapshot;
  private readonly getHovSkillHoldDurationMs: () => HovSkillHoldMs;
  private readonly getShowLockedHovSkillContent: () => boolean;
  private readonly getHovAmplifierUnlocked: () => boolean;
  private readonly getWallUnlocked: () => boolean;
  private readonly getExplorationAvailable: () => boolean;
  private readonly getOHovHovCoreGainScalingUnlocked: () => boolean;
  private readonly onHovInputLockChange: (locked: boolean) => void;
  private readonly onActiveTabChange: ((tabId: CoreLoopTabId) => void) | null;
  private activeTabId: CoreLoopTabId = "main-hover";
  private isSkillUnlocked = false;
  private isAmplifierUnlocked = false;
  private isWallUnlocked = false;
  private isExplorationAvailable = false;
  private readonly getHovOLabelAchievementUnlocked: () => boolean;
  private readonly getTimHovActivationUnlocked: () => boolean;
  private readonly getAmplifierUpgradeGuardMs: () => AmplifierUpgradeGuardMs;
  private readonly getHasSeenAvailableAmplifierUpgradeGuard: () => boolean;
  private readonly onSeenAvailableAmplifierUpgradeGuard: () => boolean;
  private readonly getHasSeenMHovButton: () => boolean;
  private readonly onSeenMHovButton: () => boolean;
  private readonly getHasSeenNHovButton: () => boolean;
  private readonly onSeenNHovButton: () => boolean;
  private readonly hovStagePanelState = new NHovStagePanelState();
  // private isGainBalanceUnlocked = false;
  private hasAppliedInitialUnlockSnapshot = false;
  private lastSnapshot: HoverIdleSnapshot | null = null;
  private lastPresentationWorthyTargetSnapshot: HoverIdleSnapshot | null = null;
  private isCurrentTargetStructurePresentationWorthy = false;
  private cancelMHovAnimation: (() => void) | null = null;
  private hovTargetRefreshFrameId: number | null = null;

  private readonly handleMainTabIntent = (): void => {
    this.setActiveTab("main-hover");
  };

  private readonly handleSkillTabIntent = (): void => {
    if (!this.isSkillUnlocked) {
      return;
    }
    this.setActiveTab("hov-skill");
  };

  private readonly handleAmplifierTabIntent = (): void => {
    if (!this.isAmplifierUnlocked) {
      return;
    }
    this.setActiveTab("hov-amplifier");
  };

  private readonly handleWallTabIntent = (): void => {
    if (!this.isWallUnlocked) {
      return;
    }
    this.setActiveTab("hov-wall");
  };

  private readonly handleExplorationTabIntent = (): void => {
    if (!this.isExplorationAvailable) {
      return;
    }
    this.setActiveTab("hov-exploration");
  };

  private readonly handleHovStagePanelCaretClick = (): void => {
    this.toggleHovStagePanel();
  };

  constructor(options: HovViewOptions) {
    const messages = getMessages();
    this.root = document.createElement("div");
    this.root.className = "hover-idle-panel";

    this.toastStack = new NoticeToastStack({
      pointerModel: options.pointerModel,
    });
    this.onHovInputLockChange =
      options.onLockHovInput ?? ((): void => undefined);

    this.coreTabs = document.createElement("div");
    this.coreTabs.className = "hover-idle-core-tabs";
    this.coreTabs.setAttribute("aria-label", messages.hovView.tabsAria);
    this.coreTabs.hidden = true;

    this.mainTabButton = createCoreTabButton(messages.hovView.mainTab);
    this.mainTabButton.dataset.coreTab = "main-hover";
    this.skillTabButton = createCoreTabButton(messages.hovView.skillTab);
    this.skillTabButton.dataset.coreTab = "hov-skill";
    this.setTabAvailability(this.skillTabButton, false);
    this.amplifierTabButton = createCoreTabButton(
      messages.hovView.amplifierTab,
    );
    this.amplifierTabButton.dataset.coreTab = "hov-amplifier";
    this.setTabAvailability(this.amplifierTabButton, false);
    this.wallTabButton = createCoreTabButton(messages.hovView.wallTab);
    this.wallTabButton.dataset.coreTab = "hov-wall";
    this.setTabAvailability(this.wallTabButton, false);
    this.explorationTabButton = createCoreTabButton(
      messages.hovView.explorationTab,
    );
    this.explorationTabButton.dataset.coreTab = "hov-exploration";
    this.setTabAvailability(this.explorationTabButton, false);
    this.addTabIntentListeners(this.mainTabButton, this.handleMainTabIntent);
    this.addTabIntentListeners(this.skillTabButton, this.handleSkillTabIntent);
    this.addTabIntentListeners(
      this.amplifierTabButton,
      this.handleAmplifierTabIntent,
    );
    this.addTabIntentListeners(this.wallTabButton, this.handleWallTabIntent);
    this.addTabIntentListeners(
      this.explorationTabButton,
      this.handleExplorationTabIntent,
    );
    this.coreTabs.append(
      this.mainTabButton,
      this.skillTabButton,
      this.amplifierTabButton,
      this.wallTabButton,
      this.explorationTabButton,
    );

    this.mainPanel = document.createElement("div");
    this.mainPanel.className = "hover-idle-target-panel";
    this.hovStage = document.createElement("div");
    this.hovStage.className = "hover-idle-hov-stage";
    this.hovStage.dataset.activePanel = "cells";
    this.hovStage.dataset.nhovVisible = "false";
    this.hovCellsPanel = document.createElement("div");
    this.hovCellsPanel.className = "hover-idle-hov-cells-panel";
    this.hovCellsPanel.dataset.hovStagePanel = "cells";
    this.nHovPanel = document.createElement("div");
    this.nHovPanel.className = "hover-idle-nhov-panel";
    this.nHovPanel.dataset.hovStagePanel = "nhov";
    this.nHovPanel.hidden = true;
    this.hovStagePanelCaretButton = document.createElement("button");
    this.hovStagePanelCaretButton.type = "button";
    this.hovStagePanelCaretButton.className = "hover-idle-hov-stage-caret";
    this.hovStagePanelCaretButton.hidden = true;
    this.hovStagePanelCaretButton.disabled = true;
    this.hovStagePanelCaretButton.dataset.hovStageCaret = "true";
    this.hovStagePanelCaretButton.addEventListener(
      "click",
      this.handleHovStagePanelCaretClick,
    );
    this.amplifierPanel = new HovAmplifierPanelView({
      pointerModel: options.pointerModel,
      onAmplifyEntryHoverChange: options.onHovAmplifierEntryHoverChange,
      onPurchaseHovAmplifierGainUpgrade:
        options.onPurchaseHovAmplifierGainUpgrade,
      getAmplifierUpgradeGuardMs: options.getAmplifierUpgradeGuardMs,
      onAmplifierUpgradeGuardMsChange: options.onAmplifierUpgradeGuardMsChange,
      onAmplifierPanelMpAssign: options.onAmplifierPanelMpAssign
        ? (entryId) => {
            if (this.isHovInputLocked) return;
            options.onAmplifierPanelMpAssign!(entryId);
          }
        : undefined,
      onAmplifierPanelMpRemove: options.onAmplifierPanelMpRemove
        ? (entryId) => {
            if (this.isHovInputLocked) return;
            options.onAmplifierPanelMpRemove!(entryId);
          }
        : undefined,
      onAmplifierUpgradeMpAssign: options.onAmplifierUpgradeMpAssign
        ? (entryId) => {
            if (this.isHovInputLocked) return;
            options.onAmplifierUpgradeMpAssign!(entryId);
          }
        : undefined,
      onAmplifierUpgradeMpRemove: options.onAmplifierUpgradeMpRemove
        ? (entryId) => {
            if (this.isHovInputLocked) return;
            options.onAmplifierUpgradeMpRemove!(entryId);
          }
        : undefined,
      getHoverToggleDurationMs: options.getHoverToggleDurationMs,
      upgradeRepeatInitialIntervalMs:
        options.getAmplifierUpgradeRepeatInitialIntervalMs,
      speedMultiplier: options.getUpgradeRepeatSpeedMultiplier,
    });

    this.resourceHud = new ResourceHudView({
      pointerModel: options.pointerModel,
      onHovOLabelHoverChange: (isHovering) => {
        options.onHovOLabelHoverChange?.(isHovering);
      },
      onTimHovActivationToggle: () => {
        options.onTimHovActivationToggle?.();
      },
      getHoverToggleDurationMs: options.getHoverToggleDurationMs,
    });
    this.hovCoreHud = new HovCoreHudView();
    this.hovCoreHud.setVisible(false);
    this.getHovOLabelAchievementUnlocked =
      options.getHovOLabelAchievementUnlocked ?? (() => false);
    this.getTimHovActivationUnlocked =
      options.getTimHovActivationUnlocked ?? (() => false);
    this.getAmplifierUpgradeGuardMs =
      options.getAmplifierUpgradeGuardMs ?? (() => 0);
    this.getHasSeenAvailableAmplifierUpgradeGuard =
      options.getHasSeenAvailableAmplifierUpgradeGuard ?? (() => false);
    this.onSeenAvailableAmplifierUpgradeGuard =
      options.onSeenAvailableAmplifierUpgradeGuard ?? (() => false);
    this.getHasSeenMHovButton = options.getHasSeenMHovButton ?? (() => false);
    this.onSeenMHovButton = options.onSeenMHovButton ?? (() => false);
    this.getHasSeenNHovButton = options.getHasSeenNHovButton ?? (() => false);
    this.onSeenNHovButton = options.onSeenNHovButton ?? (() => false);
    this.targetGrid = new HoverTargetGridView({
      variant: "hov",
      pointerModel: options.pointerModel,
      getGridTier: (snapshot) => snapshot.hovGridTier,
      getHoveringCellIndex: (snapshot) => snapshot.hovHoveringCellIndex,
      getThemeHue: (snapshot) => snapshot.hovGridThemeHue,
      getTargetCells: (snapshot) => snapshot.hovTargetCells,
      getTimHovConsumptionRatePerSecond: (snapshot, cellIndex) =>
        snapshot.hovTargetCells[cellIndex]?.timHovConsumptionRatePerSecond ?? 0,
      onCellHoverChange: (cellIndex, isHovering) => {
        if (this.isHovInputLocked) return !isHovering;
        return options.onCellHoverChange(cellIndex, isHovering);
      },
      onCellClick: (cellIndex) => {
        if (this.isHovInputLocked) return;
        options.onHovCellClick?.(cellIndex);
      },
      getMpCapacity: (snapshot) => snapshot.mpCapacity,
      onMpPlace: (cellIndex) => {
        if (this.isHovInputLocked) return;
        options.onMpPlace?.(cellIndex);
      },
      onMpRetrieve: (cellIndex) => {
        if (this.isHovInputLocked) return;
        options.onMpRetrieve?.(cellIndex);
      },
      onMpWallAssign: options.onMpWallHovTargetAssign,
      onMpWallRemove: options.onMpWallHovTargetRemove,
      getHoverToggleDurationMs: options.getHoverToggleDurationMs,
    });
    this.groupingHovGainUpgradePanel = new GroupingHovGainUpgradePanelView({
      pointerModel: options.pointerModel,
      entries: buildGroupingHovGainUpgradeCatalog({
        resolveGlobalOneCostBatchPreview:
          options.resolveGlobalHovGainUpgradeOneCostBatchPreview,
        resolveHovCellOneCostBatchPreview:
          options.resolveHovCellGainUpgradeOneCostBatchPreview,
      }),
      isHngDrawerActive: options.isHngDrawerActive,
      initialIntervalMs: options.getHovGainFamilyUpgradeRepeatInitialIntervalMs,
      speedMultiplier: options.getUpgradeRepeatSpeedMultiplier,
      onPurchaseGlobalHovGainUpgrade: () => {
        if (this.isHovInputLocked) return false;
        return options.onPurchaseGlobalHovGainUpgrade();
      },
      onPurchaseHovCellGainUpgrade: (index) => {
        if (this.isHovInputLocked) return false;
        return options.onPurchaseHovCellGainUpgrade(index);
      },
      getMotionPolicy: options.getMotionPolicy,
      getThemeHue: (snapshot) => snapshot.hovGridThemeHue,
      onPurchaseBurstStart: options.onHovGainUpgradePurchaseBurstStart,
      onPurchaseBurstEnd: options.onHovGainUpgradePurchaseBurstEnd,
      onHovGainUpgradeMpAssign: options.onHovGainUpgradeMpAssign
        ? (target) => {
            if (this.isHovInputLocked) return;
            options.onHovGainUpgradeMpAssign!(target);
          }
        : undefined,
      onHovGainUpgradeMpRemove: options.onHovGainUpgradeMpRemove
        ? (target) => {
            if (this.isHovInputLocked) return;
            options.onHovGainUpgradeMpRemove!(target);
          }
        : undefined,
      onMpWallAssign: options.onMpWallHovGainUpgradeAssign,
      onMpWallRemove: options.onMpWallHovGainUpgradeRemove,
      getHoverToggleDurationMs: options.getHoverToggleDurationMs,
    });
    this.hovCellsPanel.append(this.targetGrid.root);
    this.hovStage.append(
      this.hovCellsPanel,
      this.nHovPanel,
      this.hovStagePanelCaretButton,
    );
    this.mainPanel.append(this.hovStage, this.groupingHovGainUpgradePanel.root);
    const getOHovChargeDurationMs = (): number =>
      options.getMotionPolicy?.().oHovChargeDurationMs ?? 5000;
    this.oHovProgressView = new OHovProgressView({
      container: this.mainPanel,
      onConfirm: options.onOHovExecute,
      getConfirmDurationMs: getOHovChargeDurationMs,
    });
    this.mainPanel.insertBefore(
      this.oHovProgressView.root,
      this.mainPanel.firstChild,
    );

    const getMHovSpeed = (): MHovSpeedSetting =>
      options.getMotionPolicy?.().mHovAnimationSpeed ??
      options.getMHovSpeed?.() ??
      "normal";
    const getMHovChargeDurationMs = (): number =>
      options.getMotionPolicy?.().mHovChargeDurationMs ??
      options.getMHovChargeDurationMs?.() ??
      0;
    const onMHovExecute = options.onMHovExecute ?? (() => undefined);
    this.mHovView = new MHovView({
      container: this.hovCellsPanel,
      onExecute: () => {
        const snap = this.lastSnapshot;
        if (!snap) return undefined;
        return new Promise<void>((resolve, reject) => {
          let completedSynchronously = false;
          let cancelAnimation: (() => void) | null = null;
          const complete = (): void => {
            this.cancelMHovAnimation = null;
            try {
              Promise.resolve(onMHovExecute()).then(() => {
                this.schedulePostHovTargetHoverRefresh();
                resolve();
              }, reject);
            } catch (error) {
              reject(error);
            }
          };

          cancelAnimation = runMHovAnimation({
            gridRoot: this.targetGrid.root,
            targetCells: snap.hovTargetCells,
            gridTier: snap.hovGridTier,
            mode: getMHovSpeed(),
            onComplete: () => {
              completedSynchronously = cancelAnimation === null;
              complete();
            },
          });
          this.cancelMHovAnimation = completedSynchronously
            ? null
            : cancelAnimation;
        });
      },
      getChargeDurationMs: getMHovChargeDurationMs,
    });
    this.hovCellsPanel.insertBefore(this.mHovView.root, this.targetGrid.root);

    const onNHovExecute =
      options.onNHovExecute ?? ((): boolean | void => undefined);
    this.nHovView = new NHovView({
      container: this.nHovPanel,
      onExecute: (branchId) => {
        let didCommit: boolean | void;
        try {
          didCommit = onNHovExecute(branchId);
        } finally {
          this.showHovCellsPanel();
        }
        return didCommit;
      },
      getHoldDurationMs: () =>
        options.getMotionPolicy?.().nHovChargeDurationMs ?? 5000,
      getAnimationMode: () => {
        const speed =
          options.getMotionPolicy?.().nHovAnimationSpeed ?? "normal";
        return speed === "instant" ? "none" : speed;
      },
      onLockHovInput: (locked) => {
        this.setHovInputLocked(locked);
      },
      onExecutionStateChange: (state) => {
        this.setNHovExecutionState(state);
      },
    });

    this.wallPanel = document.createElement("div");
    this.wallPanel.className = "hover-idle-hov-wall-panel";
    this.wallPanel.hidden = true;
    this.explorationPanel = document.createElement("div");
    this.explorationPanel.className = "hover-idle-exploration-panel";
    this.explorationPanel.setAttribute("role", "region");
    this.explorationPanel.setAttribute(
      "aria-label",
      messages.hovView.explorationTab,
    );
    this.explorationPanel.hidden = true;
    this.root.append(
      this.resourceHud.root,
      this.hovCoreHud.root,
      this.coreTabs,
      this.mainPanel,
      this.amplifierPanel.root,
      this.wallPanel,
      this.explorationPanel,
    );
    this.skillView = new HovSkillView({
      container: this.root,
      onAcquireRequest: options.onHovSkillAcquire,
      holdDurationMs: options.getHovSkillHoldDurationMs(),
      onHoldDurationChange: options.onHovSkillHoldDurationChange,
    });
    this.getHovSkillSnapshot = options.getHovSkillSnapshot;
    this.getHovSkillHoldDurationMs = options.getHovSkillHoldDurationMs;
    this.getShowLockedHovSkillContent =
      options.getShowLockedHovSkillContent ?? (() => false);
    this.getHovAmplifierUnlocked = options.getHovAmplifierUnlocked;
    this.getWallUnlocked = options.getWallUnlocked ?? (() => false);
    this.getExplorationAvailable =
      options.getExplorationAvailable ?? (() => false);
    this.getOHovHovCoreGainScalingUnlocked =
      options.getOHovHovCoreGainScalingUnlocked ?? (() => false);
    this.onActiveTabChange = options.onActiveTabChange ?? null;
    options.container.append(this.root);
    this.syncHovStagePanelAttributes();
    this.syncActiveTab();
  }

  update(
    snapshot: HoverIdleSnapshot,
    recentHovRatePerSecond: HugeNumberInput = 0,
    recentHovCoreRatePerHour: HugeNumberInput = 0,
    options: HovViewUpdateOptions = {},
  ): void {
    this.lastSnapshot = snapshot;
    const readoutSnapshot = options.readoutSnapshot ?? snapshot;
    const targetStructureSnapshot = options.targetStructureSnapshot ?? snapshot;
    const targetStructureAction =
      options.targetStructureAction ?? "commit-snapshot";
    const updateTargetThemeHue = options.updateTargetThemeHue !== false;
    this.resourceHud.update(
      readoutSnapshot,
      recentHovRatePerSecond,
      this.getHovOLabelAchievementUnlocked(),
      this.getTimHovActivationUnlocked(),
    );
    this.hovCoreHud.update(readoutSnapshot, recentHovCoreRatePerHour);
    if (targetStructureAction === "commit-snapshot") {
      this.commitTargetStructureSnapshot(
        targetStructureSnapshot,
        options.deferTargetStructureHoverRefresh === true,
      );
    } else if (updateTargetThemeHue) {
      this.holdLastPresentationWorthyTargetStructure(targetStructureSnapshot);
    }
    this.groupingHovGainUpgradePanel.update(readoutSnapshot);
    this.mHovView.update({
      mHov: readoutSnapshot.mHov,
      themeHue: snapshot.hovGridThemeHue,
    });
    this.nHovView.update(readoutSnapshot);
    this.syncNHovPanelVisibility();
    this.latchSeenResetButtons(readoutSnapshot);
    this.oHovProgressView.update(readoutSnapshot.oHov);
    const amplifierUnlocked = this.getHovAmplifierUnlocked();
    let hasSeenAvailableAmplifierUpgradeGuard =
      this.getHasSeenAvailableAmplifierUpgradeGuard();
    if (
      !hasSeenAvailableAmplifierUpgradeGuard &&
      shouldUnlockAmplifierUpgradeGuardUi({
        amplifierUnlocked,
        entries: snapshot.hovAmplifier.entries,
      })
    ) {
      hasSeenAvailableAmplifierUpgradeGuard =
        this.onSeenAvailableAmplifierUpgradeGuard() ||
        this.getHasSeenAvailableAmplifierUpgradeGuard();
    }
    this.amplifierPanel.update(snapshot.hovAmplifier, {
      amplifierUnlocked,
      amplifierUpgradeGuardMs: this.getAmplifierUpgradeGuardMs(),
      hasSeenAvailableAmplifierUpgradeGuard,
      mpPlacement: {
        sectionUnlocked: snapshot.mpm.amplifierPanels.unlocked,
        sectionEnabled: snapshot.mpm.amplifierPanels.enabled,
        availableMp: computeMpmAvailableMpFromOccupancy({
          mpCapacity: snapshot.mpCapacity,
          state: snapshot.mpm,
        }),
        assignedEntryIds: new Set<HovAmplifierEntryId>(
          snapshot.mpm.amplifierPanels.assignments,
        ),
      },
      upgradeMpPlacement: {
        sectionUnlocked: snapshot.mpm.amplifierUpgrades.unlocked,
        sectionEnabled: snapshot.mpm.amplifierUpgrades.enabled,
        availableMp: computeMpmAvailableMpFromOccupancy({
          mpCapacity: snapshot.mpCapacity,
          state: snapshot.mpm,
        }),
        assignedEntryIds: new Set<HovAmplifierEntryId>(
          snapshot.mpm.amplifierUpgrades.assignments,
        ),
      },
    });
    this.syncSkillUnlock(snapshot.unlocks.tabs.hovSkill);
    this.syncAmplifierUnlock(amplifierUnlocked);
    this.syncWallUnlock(this.getWallUnlocked());
    this.syncExplorationAvailability(this.getExplorationAvailable());
    // this.syncGainBalanceUnlock(snapshot.unlocks.tabs.hovTimGainBalance);
    this.syncCoreTabsVisibility();
    const hovSkillSnapshot = this.getHovSkillSnapshot();
    const ohovHovCoreGainScalingUnlocked =
      this.getOHovHovCoreGainScalingUnlocked();
    this.skillView.update({
      resources: toHovSkillResourceBalances(snapshot),
      snapshot: hovSkillSnapshot,
      holdDurationMs: this.getHovSkillHoldDurationMs(),
      amplifierGainOHovMultiplier: resolveAmplifierGainOHovPreviewMultiplier({
        oHovPower: snapshot.oHov.power,
      }),
      ohovHovCoreGainMultiplier: resolveOHovHovCoreGainPreviewMultiplier({
        achievementScalingUnlocked: ohovHovCoreGainScalingUnlocked,
        oHovPower: snapshot.oHov.power,
      }),
      ohovHovCoreGainScalingUnlocked,
      mpCapacityCoreOHovHovCoreGainMultiplier:
        resolveMpCapacityCoreOHovHovCoreGainMultiplier({
          mpCapacity: snapshot.mpCapacity,
        }),
      freeMpCoreOHovHovCoreGainMultiplier:
        resolveFreeMpCoreOHovHovCoreGainMultiplier({
          freeMp: computeMpmAvailableMpFromOccupancy({
            mpCapacity: snapshot.mpCapacity,
            state: snapshot.mpm,
          }),
        }),
      raBambooMultiplier: resolveRaBambooAmplifierPreviewMultiplier({
        clearedWallKindCount: countClearedWallKinds(snapshot),
      }),
      raPineMultiplier: resolveRaPineAmplifierPreviewMultiplier({
        oHovCurrentRunGameElapsedMs: snapshot.oHov.currentRunGameElapsedMs,
      }),
      apDeerExponentBonus:
        snapshot.hovAmplifier.apExponentBreakdown.apDeerExponentBonus,
      apButterflyExponentBonus:
        snapshot.hovAmplifier.apExponentBreakdown.apButterflyExponentBonus,
      showLockedContent: this.getShowLockedHovSkillContent(),
    });
    this.hasAppliedInitialUnlockSnapshot = true;
  }

  seedPresentationWorthyTargetStructure(snapshot: HoverIdleSnapshot): boolean {
    if (!isPresentationWorthyTargetStructure(snapshot)) {
      return false;
    }
    this.lastPresentationWorthyTargetSnapshot = snapshot;
    return true;
  }

  private commitTargetStructureSnapshot(
    snapshot: HoverIdleSnapshot,
    deferTargetStructureHoverRefresh: boolean,
  ): void {
    if (deferTargetStructureHoverRefresh) {
      this.targetGrid.update(snapshot, {
        refreshHoverIntentsAfterStructureChange: false,
      });
    } else {
      this.targetGrid.update(snapshot);
    }
    this.recordTargetStructureCommit(snapshot);
  }

  private holdLastPresentationWorthyTargetStructure(
    latestSnapshot: HoverIdleSnapshot,
  ): void {
    if (
      this.isCurrentTargetStructurePresentationWorthy ||
      this.lastPresentationWorthyTargetSnapshot === null
    ) {
      this.targetGrid.updateThemeHue(latestSnapshot.hovGridThemeHue);
      return;
    }

    const heldSnapshot = withLatestTargetThemeHue(
      this.lastPresentationWorthyTargetSnapshot,
      latestSnapshot,
    );
    this.targetGrid.update(heldSnapshot, {
      refreshHoverIntentsAfterStructureChange: false,
    });
    this.isCurrentTargetStructurePresentationWorthy = true;
  }

  private recordTargetStructureCommit(snapshot: HoverIdleSnapshot): void {
    const isPresentationWorthy = isPresentationWorthyTargetStructure(snapshot);
    this.isCurrentTargetStructurePresentationWorthy = isPresentationWorthy;
    if (isPresentationWorthy) {
      this.lastPresentationWorthyTargetSnapshot = snapshot;
    }
  }

  /** Current or planned visual state of the manual OHov button. */
  getOutsideResetOrigin(): OHovManualExecutionButtonVisual {
    return this.oHovProgressView.getManualExecutionButtonVisual();
  }

  restoreVirtualPointerFromHoverAnchor(): boolean {
    if (this.activeTabId !== "main-hover") {
      return false;
    }
    return this.targetGrid.restoreVirtualPointerFromHoverAnchor();
  }

  showHovTargetScreen(): void {
    this.setActiveTab("main-hover");
    this.showHovCellsPanel();
  }

  refreshHovTargetHoverIntents(): void {
    if (this.activeTabId !== "main-hover") {
      return;
    }
    this.targetGrid.refreshHoverIntents();
  }

  // private syncGainBalanceUnlock(unlocked: boolean): void {
  //   if (unlocked === this.isGainBalanceUnlocked) {
  //     return;
  //   }
  //   const shouldAnnounce = unlocked && this.hasAppliedInitialUnlockSnapshot;
  //   this.isGainBalanceUnlocked = unlocked;
  //   if (shouldAnnounce) {
  //     this.showNotice(getMessages().balance.gainUnlockNotice, {
  //       dismissAfterHoverMs: 1500,
  //     });
  //   }
  // }

  private syncSkillUnlock(unlocked: boolean): void {
    if (unlocked === this.isSkillUnlocked) {
      return;
    }
    const shouldAnnounce = unlocked && this.hasAppliedInitialUnlockSnapshot;
    this.isSkillUnlocked = unlocked;
    this.setTabAvailability(this.skillTabButton, unlocked);
    if (!unlocked && this.activeTabId === "hov-skill") {
      this.setActiveTab("main-hover");
    } else {
      this.syncActiveTab();
    }
    if (shouldAnnounce) {
      this.showNotice(getMessages().hovView.skillTabUnlockNotice, {
        dismissAfterHoverMs: 100,
      });
    }
  }

  private syncAmplifierUnlock(unlocked: boolean): void {
    if (unlocked === this.isAmplifierUnlocked) {
      return;
    }
    const shouldAnnounce = unlocked && this.hasAppliedInitialUnlockSnapshot;
    this.isAmplifierUnlocked = unlocked;
    this.setTabAvailability(this.amplifierTabButton, unlocked);
    if (!unlocked && this.activeTabId === "hov-amplifier") {
      this.setActiveTab("main-hover");
    } else {
      this.syncActiveTab();
    }
    if (shouldAnnounce) {
      this.showNotice(getMessages().hovView.amplifierTabUnlockNotice, {
        dismissAfterHoverMs: 100,
      });
    }
  }

  private syncWallUnlock(unlocked: boolean): void {
    if (unlocked === this.isWallUnlocked) {
      return;
    }
    const shouldAnnounce = unlocked && this.hasAppliedInitialUnlockSnapshot;
    this.isWallUnlocked = unlocked;
    this.setTabAvailability(this.wallTabButton, unlocked);
    if (!unlocked && this.activeTabId === "hov-wall") {
      this.setActiveTab("main-hover");
    } else {
      this.syncActiveTab();
    }
    if (shouldAnnounce) {
      this.showNotice(getMessages().hovView.wallTabUnlockNotice, {
        dismissAfterHoverMs: 100,
      });
    }
  }

  private syncExplorationAvailability(available: boolean): void {
    if (available === this.isExplorationAvailable) {
      return;
    }
    this.isExplorationAvailable = available;
    this.setTabAvailability(this.explorationTabButton, available);
    if (!available && this.activeTabId === "hov-exploration") {
      this.setActiveTab("main-hover");
    } else {
      this.syncActiveTab();
    }
  }

  /**
   * Container for the in-tab Wall surface. The Hov view owns the panel slot
   * and tab plumbing; createGameApp mounts WallView into this container.
   */
  getWallTabContainer(): HTMLDivElement {
    return this.wallPanel;
  }

  getExplorationTabContainer(): HTMLDivElement {
    return this.explorationPanel;
  }

  showNotice(message: string, options?: ShowToastOptions): void {
    this.toastStack.show(message, options);
  }

  clearNotice(): void {
    this.toastStack.clearAll();
  }

  dispose(): void {
    this.removeTabIntentListeners(this.mainTabButton, this.handleMainTabIntent);
    this.removeTabIntentListeners(
      this.skillTabButton,
      this.handleSkillTabIntent,
    );
    this.removeTabIntentListeners(
      this.amplifierTabButton,
      this.handleAmplifierTabIntent,
    );
    this.removeTabIntentListeners(this.wallTabButton, this.handleWallTabIntent);
    this.removeTabIntentListeners(
      this.explorationTabButton,
      this.handleExplorationTabIntent,
    );
    this.cancelMHovAnimation?.();
    this.cancelMHovAnimation = null;
    this.setNHovExecutionState(null);
    this.setHovInputLocked(false);
    this.hovStagePanelCaretButton.removeEventListener(
      "click",
      this.handleHovStagePanelCaretClick,
    );
    if (this.hovTargetRefreshFrameId !== null) {
      cancelAnimationFrame(this.hovTargetRefreshFrameId);
      this.hovTargetRefreshFrameId = null;
    }
    this.resourceHud.dispose();
    this.hovCoreHud.dispose();
    this.targetGrid.dispose();
    this.groupingHovGainUpgradePanel.dispose();
    this.mHovView.dispose();
    this.nHovView.dispose();
    this.oHovProgressView.dispose();
    this.amplifierPanel.dispose();
    this.skillView.dispose();
    this.toastStack.dispose();
    this.root.remove();
  }

  private addTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.addEventListener("pointerenter", handler);
    button.addEventListener("focus", handler);
    button.addEventListener("click", handler);
  }

  private removeTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.removeEventListener("pointerenter", handler);
    button.removeEventListener("focus", handler);
    button.removeEventListener("click", handler);
  }

  private setActiveTab(tabId: CoreLoopTabId): void {
    if (this.activeTabId === tabId) {
      return;
    }

    this.activeTabId = tabId;
    this.syncActiveTab();
    this.onActiveTabChange?.(tabId);
  }

  private syncActiveTab(): void {
    const isMainActive = this.activeTabId === "main-hover";
    const isSkillActive = this.activeTabId === "hov-skill";
    const isAmplifierActive = this.activeTabId === "hov-amplifier";
    const isWallActive = this.activeTabId === "hov-wall";
    const isExplorationActive = this.activeTabId === "hov-exploration";
    const isHovCoreHudActive = isSkillActive || isAmplifierActive;
    this.mainPanel.hidden = !isMainActive;
    this.skillView.setVisible(isSkillActive);
    this.amplifierPanel.root.hidden = !isAmplifierActive;
    this.wallPanel.hidden = !isWallActive;
    this.explorationPanel.hidden = !isExplorationActive;
    this.resourceHud.root.hidden = isHovCoreHudActive || isExplorationActive;
    this.hovCoreHud.setVisible(isHovCoreHudActive);
    if (!isAmplifierActive) {
      this.amplifierPanel.clearHover();
    }
    this.mainTabButton.dataset.active = isMainActive ? "true" : "false";
    this.skillTabButton.dataset.active = isSkillActive ? "true" : "false";
    this.amplifierTabButton.dataset.active = isAmplifierActive
      ? "true"
      : "false";
    this.wallTabButton.dataset.active = isWallActive ? "true" : "false";
    this.explorationTabButton.dataset.active = isExplorationActive
      ? "true"
      : "false";
    this.mainTabButton.setAttribute(
      "aria-selected",
      isMainActive ? "true" : "false",
    );
    this.skillTabButton.setAttribute(
      "aria-selected",
      isSkillActive ? "true" : "false",
    );
    this.amplifierTabButton.setAttribute(
      "aria-selected",
      isAmplifierActive ? "true" : "false",
    );
    this.wallTabButton.setAttribute(
      "aria-selected",
      isWallActive ? "true" : "false",
    );
    this.explorationTabButton.setAttribute(
      "aria-selected",
      isExplorationActive ? "true" : "false",
    );
  }

  private setTabAvailability(
    button: HTMLButtonElement,
    unlocked: boolean,
  ): void {
    button.hidden = !unlocked;
    button.dataset.locked = unlocked ? "false" : "true";
    button.disabled = !unlocked;
    button.setAttribute("aria-disabled", unlocked ? "false" : "true");
  }

  setWallTabDot(visible: boolean): void {
    this.wallTabButton.dataset.unseen = visible ? "true" : "false";
  }

  private syncCoreTabsVisibility(): void {
    this.coreTabs.hidden = !(
      this.isSkillUnlocked ||
      this.isAmplifierUnlocked ||
      this.isWallUnlocked ||
      this.isExplorationAvailable
    );
  }

  private syncNHovPanelVisibility(): void {
    const isVisible = !this.nHovView.root.hidden;
    this.nHovPanel.hidden = !isVisible;
    this.hovStagePanelState.setNHovVisible(isVisible);
    this.syncHovStagePanelAttributes();
  }

  // Latches the persistent "reset button displayed" settings flags from the
  // same flags that drive the MHov / NHov views, so the latch matches what the
  // player actually sees without re-deriving the display predicate.
  private latchSeenResetButtons(snapshot: HoverIdleSnapshot): void {
    if (snapshot.mHov.shouldShow && !this.getHasSeenMHovButton()) {
      this.onSeenMHovButton();
    }
    const nHovDisplayed = snapshot.nHov.isUnlocked && snapshot.nHov.canExecute;
    if (nHovDisplayed && !this.getHasSeenNHovButton()) {
      this.onSeenNHovButton();
    }
  }

  private toggleHovStagePanel(): void {
    if (!this.hovStagePanelState.isNHovVisible) {
      return;
    }
    if (this.hovStagePanelState.isNHovExecuting) {
      return;
    }
    if (this.hovStagePanelState.activePanel === "cells") {
      this.hovStagePanelState.showNHovPanel();
    } else {
      this.hovStagePanelState.showCellsPanel();
    }
    this.syncHovStagePanelAttributes();
  }

  private showHovCellsPanel(): void {
    this.hovStagePanelState.showCellsPanel();
    this.syncHovStagePanelAttributes();
  }

  private syncHovStagePanelAttributes(): void {
    const activePanel = this.hovStagePanelState.activePanel;
    const isNHovVisible = this.hovStagePanelState.isNHovVisible;
    const isNHovExecuting = this.hovStagePanelState.isNHovExecuting;
    const executionPhase = this.hovStagePanelState.nHovExecutionState?.phase;
    const messages = getMessages().hovView.nHov;
    const goesToNHov = activePanel === "cells";

    this.hovStage.dataset.activePanel = activePanel;
    this.hovStage.dataset.nhovVisible = isNHovVisible ? "true" : "false";
    this.hovStage.dataset.nhovExecuting = isNHovExecuting ? "true" : "false";
    this.hovCellsPanel.dataset.nhovExecuting = isNHovExecuting
      ? "true"
      : "false";
    if (executionPhase === undefined) {
      this.hovStage.removeAttribute("data-nhov-execution-phase");
      this.hovCellsPanel.removeAttribute("data-nhov-execution-phase");
    } else {
      this.hovStage.dataset.nhovExecutionPhase = executionPhase;
      this.hovCellsPanel.dataset.nhovExecutionPhase = executionPhase;
    }
    this.hovStagePanelCaretButton.hidden = !isNHovVisible;
    this.hovStagePanelCaretButton.disabled = !isNHovVisible || isNHovExecuting;
    this.hovStagePanelCaretButton.dataset.direction = goesToNHov
      ? "next"
      : "previous";
    this.hovStagePanelCaretButton.setAttribute(
      "aria-label",
      goesToNHov
        ? messages.goToNHovPanelAriaLabel
        : messages.returnToCellsPanelAriaLabel,
    );
    this.hovStagePanelCaretButton.setAttribute(
      "aria-disabled",
      isNHovVisible && !isNHovExecuting ? "false" : "true",
    );
    this.hovStagePanelCaretButton.textContent = goesToNHov
      ? "\u203a"
      : "\u2039";
  }

  private setNHovExecutionState(state: NHovExecutionState): void {
    this.hovStagePanelState.setNHovExecutionState(state);
    this.syncHovStagePanelAttributes();
  }

  pulseHovGainUpgradeTarget(
    target: HovGainUpgradeMpTarget,
    options: { readonly isMilestone?: boolean } = {},
  ): void {
    this.groupingHovGainUpgradePanel.pulseEntry(target, options);
  }

  private setHovInputLocked(locked: boolean): void {
    if (locked === this.isHovInputLocked) {
      return;
    }
    this.isHovInputLocked = locked;
    this.hovCellsPanel.toggleAttribute("inert", locked);
    this.groupingHovGainUpgradePanel.root.toggleAttribute("inert", locked);
    this.amplifierPanel.root.toggleAttribute("inert", locked);
    this.targetGrid.setInputLocked(locked);
    this.groupingHovGainUpgradePanel.setInputLocked(locked);
    this.amplifierPanel.setInputLocked(locked);
    this.mHovView.setInputLocked(locked);
    this.onHovInputLockChange(locked);
    if (!locked) {
      this.schedulePostHovTargetHoverRefresh();
    }
  }

  private schedulePostHovTargetHoverRefresh(): void {
    if (this.hovTargetRefreshFrameId !== null) {
      cancelAnimationFrame(this.hovTargetRefreshFrameId);
    }
    this.hovTargetRefreshFrameId = requestAnimationFrame(() => {
      this.hovTargetRefreshFrameId = null;
      this.targetGrid.refreshHoverIntents();
    });
  }
}

function toHovSkillResourceBalances(
  snapshot: HoverIdleSnapshot,
): HovSkillResourceBalances {
  return {
    hovCore: snapshot.oHov.hovCore,
  };
}

function countClearedWallKinds(snapshot: HoverIdleSnapshot): number {
  return snapshot.wall.walls.reduce(
    (count, wall) => (wall.bestClearMs !== null ? count + 1 : count),
    0,
  );
}

function createCoreTabButton(label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "hover-idle-core-tab";
  button.textContent = label;
  return button;
}
